import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';

export interface AlphaFeatureGates {
  clients: boolean;
  trips: boolean;
  pipeline: boolean;
  dashboard: boolean;
  proposals: boolean;
  isAdmin: boolean;
}

// Default gates - all disabled for non-admins (conservative default)
const DEFAULT_GATES: AlphaFeatureGates = {
  clients: false,
  trips: false,
  pipeline: false,
  dashboard: false,
  proposals: false,
  isAdmin: false,
};

/**
 * Hook to fetch alpha feature gate status for the current user.
 * Returns which features are accessible based on user role and admin configuration.
 *
 * - Admin/System Admin users always have full access (all gates return true)
 * - Non-admin users see the configured gate status
 * - While loading, all gates default to false (conservative)
 */
export function useAlphaFeatureGates() {
  const { isAuthenticated, user } = useAuth();

  const { data, isLoading, error, refetch } = useQuery<AlphaFeatureGates>({
    queryKey: ['/api/feature-gates'],
    queryFn: async () => {
      const response = await fetch('/api/feature-gates', {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch feature gates');
      }
      return response.json();
    },
    enabled: isAuthenticated,
    staleTime: 30 * 1000, // 30 seconds - refresh frequently during alpha
    gcTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: true, // Refresh when user returns to window
    refetchOnMount: true, // Always refetch on component mount
    retry: 2,
  });

  // If user is admin based on local auth context, return all enabled
  // This provides immediate access without waiting for API
  const isAdminFromContext = user?.role === 'SYSTEM_ADMIN' || user?.role === 'ADMIN';

  if (isAdminFromContext) {
    return {
      gates: {
        clients: true,
        trips: true,
        pipeline: true,
        dashboard: true,
        proposals: true,
        isAdmin: true,
      } as AlphaFeatureGates,
      isLoading: false,
      error: null,
      refetch,
    };
  }

  return {
    gates: data ?? DEFAULT_GATES,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Helper to check if a specific feature is gated (disabled) for the current user.
 * Returns true if the feature is BLOCKED (should show "Coming soon").
 */
export function isFeatureGated(
  gates: AlphaFeatureGates,
  feature: keyof Omit<AlphaFeatureGates, 'isAdmin'>
): boolean {
  // Admins are never gated
  if (gates.isAdmin) return false;
  // Feature is gated if its gate value is false
  return !gates[feature];
}
